/**
 * Install marketplace templates as read-only system-owned indicators in IndexedDB.
 */

import { persistBuildFromCompile } from "./build-persist";
import { defaultCsprojXml } from "./csproj";
import {
  createNewIndicatorId,
  findProjectBySourceTemplateId,
  getBuild,
  getFile,
  saveProjectWithFiles,
} from "./db";
import { byteSize } from "./limits";
import {
  DEFAULT_CSPROJ_PATH,
  DEFAULT_ENTRY_FILE,
  DEFAULT_README_FILE,
} from "./project-fs";
import { buildTemplateReadme } from "./studio-catalog";
import { compileIndicatorProject } from "./runtime/IndicatorRuntimeManager";
import type { IndicatorTemplate } from "./templates";
import type { CustomIndicatorProject } from "./types";

export type InstallMarketplaceResult =
  | { ok: true; projectId: string; alreadyInstalled?: boolean }
  | { ok: false; error: string };

function templateTags(template: IndicatorTemplate): string[] {
  const tags = new Set<string>(["marketplace", "system", "read-only"]);
  if (template.overlay) tags.add("overlay");
  else tags.add("pane");
  tags.add("indicator");
  return [...tags];
}

export async function installMarketplaceTemplate(
  template: IndicatorTemplate
): Promise<InstallMarketplaceResult> {
  const existing = await findProjectBySourceTemplateId(template.id);
  if (existing) {
    // Only treat as installed when a compiled build was actually persisted.
    // Otherwise fall through and finish the install for the existing project.
    const build = await getBuild(existing.id);
    if (build) {
      return { ok: true, projectId: existing.id, alreadyInstalled: true };
    }
  }

  const id = existing?.id ?? createNewIndicatorId();
  const now = Date.now();
  const readme = buildTemplateReadme(template);

  const project: CustomIndicatorProject = {
    id,
    name: template.name,
    version: "1.0",
    description: template.description ?? "",
    overlay: template.overlay,
    tags: templateTags(template),
    kind: "indicator",
    entryFilePath: DEFAULT_ENTRY_FILE,
    csprojFilePath: DEFAULT_CSPROJ_PATH,
    sizeBytes: byteSize(readme),
    entryContent: undefined,
    owner: "system",
    source: "marketplace",
    readOnly: true,
    hasSource: false,
    publisher: "System",
    installedAt: now,
    sourceTemplateId: template.id,
    createdAt: now,
    updatedAt: now,
  };

  const csprojXml = defaultCsprojXml({
    name: template.name,
    kind: "indicator",
    version: "1.0",
    description: template.description ?? "",
    overlay: template.overlay,
    entryFile: DEFAULT_ENTRY_FILE,
  });

  // Compile first; only persist the project (and its build) on success so a
  // failed compile never leaves an orphan project that blocks reinstalling.
  let compileResult;
  try {
    compileResult = await compileIndicatorProject({
      projectId: id,
      csprojXml,
      files: { [DEFAULT_ENTRY_FILE]: template.code },
    });
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : "Compile failed during install",
    };
  }

  if (!compileResult.success) {
    const logs = compileResult.logs?.join("\n");
    return {
      ok: false,
      error: logs || "Compile failed during marketplace install",
    };
  }

  await saveProjectWithFiles(project, [
    { path: DEFAULT_README_FILE, content: readme, type: "file" },
  ]);
  await persistBuildFromCompile(id, compileResult);

  return { ok: true, projectId: id };
}

/** README for an installed marketplace project (no source exposure). */
export async function loadInstalledMarketplaceReadme(
  project: CustomIndicatorProject
): Promise<string> {
  if (project.sourceTemplateId) {
    const file = await getFile(project.id, DEFAULT_README_FILE);
    if (file?.content?.trim()) return file.content;
  }
  return buildTemplateReadme({
    id: project.sourceTemplateId ?? project.id,
    name: project.name,
    description: project.description,
    overlay: project.overlay,
    code: "",
  });
}
